import { ClerkProvider as ClerkProviderBase, useAuth } from "@clerk/react";
import { useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { setAuthTokenGetter } from "@/lib/api";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

interface ClerkProviderProps {
  children: ReactNode;
}

export function ClerkProvider({ children }: ClerkProviderProps) {
  const navigate = useNavigate();

  return (
    <ClerkProviderBase
      publishableKey={PUBLISHABLE_KEY}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
    >
      <ApiAuth>{children}</ApiAuth>
    </ClerkProviderBase>
  );
}

/** Longest a request waits for Clerk to load before going out without a token. */
const TOKEN_WAIT_MS = 10_000;

/** Hands Clerk's token getter to the API client. Set during render so it's in place before any query runs. */
function ApiAuth({ children }: { children: ReactNode }) {
  const { getToken } = useAuth();
  setAuthTokenGetter(() =>
    // getToken waits for Clerk to load, which never happens if Clerk is blocked;
    // don't let that hang public requests. A token failure becomes a clean 401.
    Promise.race([
      getToken().catch(() => null),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), TOKEN_WAIT_MS)),
    ]),
  );
  return <>{children}</>;
}
